import { downloadSupplyReport } from '@gozoki/api'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { useMutation } from '@tanstack/react-query'
import { FileSpreadsheet } from 'lucide-react'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { getErrorMessageWithSentry } from '../../utils/sentry'
import { useToast } from '../communication/Toast'

// Rapport réseau « Commandé vs Reçu » (V1) — un seul XLSX toutes boutiques.
const SupplyReportButton = () => {
  const [open, setOpen] = useState(false)
  const [dateStart, setDateStart] = useState<DateTime | null>(DateTime.now().minus({ days: 7 }))
  const [dateEnd, setDateEnd] = useState<DateTime | null>(DateTime.now())
  const [Toast, showToast] = useToast()

  const { mutate, isPending } = useMutation({
    mutationFn: downloadSupplyReport,
    onError: (error) => {
      showToast({ severity: 'error', message: getErrorMessageWithSentry(error) })
    },
    onSuccess: (response, { dateStart: start, dateEnd: end }) => {
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.download = `rapport_commande_recu_${start}_${end}.xlsx`
      link.click()
      window.URL.revokeObjectURL(url)
      setOpen(false)
    },
  })

  const isRangeValid = dateStart !== null && dateEnd !== null && dateStart <= dateEnd

  const handleDownload = () => {
    if (dateStart === null || dateEnd === null || dateStart > dateEnd) return
    // Garde-fou volumétrie (spec) : au-delà de ~3 mois le fichier réseau devient lourd.
    const rangeDays = dateEnd.diff(dateStart, 'days').days
    if (rangeDays > 92) {
      const ok = window.confirm(
        `La période dépasse 3 mois (${Math.round(rangeDays)} jours) : le fichier peut être volumineux et long à générer. Continuer ?`,
      )
      if (!ok) return
    }
    mutate({
      dateStart: dateStart.toFormat('yyyy-MM-dd'),
      dateEnd: dateEnd.toFormat('yyyy-MM-dd'),
    })
  }

  return (
    <>
      <Toast />
      <Button variant="contained" sx={{ height: '36px', gap: 1 }} onClick={() => setOpen(true)}>
        <FileSpreadsheet size={18} />
        Rapport commandé / reçu
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Rapport commandé / reçu</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <DialogContentText>
            Un seul fichier XLSX pour tout le réseau : quantités commandées (expression de besoin),
            reçues (scans en boutique) et delta, par jour, boutique et produit.
          </DialogContentText>
          <DatePicker label="Du" value={dateStart} onChange={setDateStart} format="dd/MM/yyyy" />
          <DatePicker label="Au" value={dateEnd} onChange={setDateEnd} format="dd/MM/yyyy" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleDownload}
            disabled={!isRangeValid || isPending}
          >
            {isPending ? 'Génération…' : 'Télécharger'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default SupplyReportButton
